/**
 * PHARMAKON — the arithmetic of a supplier avoir (refonte Achats · Stock WP-28 / WP-29; design/ux/
 * 03_avoirs_bc_reclamations.md §1.15). Pure, integer centimes, no I/O.
 *
 *   F1  quantity still claimable as Manquant on a facture line
 *   F4  PU net HT (read from the facture line — `prix_achat_ht_net`)
 *   F5  value of an avoir line: HT = qté × PU net HT, TVA = HT × taux, TTC = HT + TVA, each rounded
 *       once to the centime; totals = Σ rounded lines (fixes TTC = HT, AUD-AVOIR-05)
 *   OP-03-06  allocation of an écart TOTAL TTC over several factures: oldest first, each up to its
 *             remaining écart; what is left is a fournisseur-level credit — superseded for new
 *             avoirs by D-38 / A-81 (WP-81, `planCreditFournisseur`); kept for the reports of the
 *             avoirs written before it
 *   OP-03-07  HT / TVA split of an écart amount (TTC authoritative)
 *   D-38 / A-81  the « crédit fournisseur » over 0..n factures (`planCreditFournisseur`):
 *             « Montant global » (references only over two or more factures, imputed up to the open
 *             net dû over one, a fournisseur-level credit over none) or « Répartition par facture »
 *             (typed parts, each imputed up to its facture's open net dû, the rest fournisseur-level)
 *   CP §8.2   the suggested amount from the factures' positive écarts (`suggestionEcart`) — never
 *             negative, never an eligibility condition or a cap
 *   WP-84     avoir produits lines v2 (D-33, A-85, A-111): the eligible quantity of an invoice line
 *             (`eligibleRestantLigne` — Qté payée − Σ live avoir lines, every motif) and the « Manquant :
 *             facturé non reçu » correction limited to the units that entered and remain
 *             (`correctionFactureNonRecu`, A-84)
 *   TWP-57    « Imputer le crédit » of an avoir (Trésorerie TO-11, TA-92): what an open facture can
 *             still take of a fournisseur-level credit (`plafondImputationCredit`), the oldest-first
 *             proposal over the open factures (`proposerImputationCredit`) and the HT / TVA the
 *             imputed parts carry (`ventilerImputationCredit`, pro rata of the avoir's own remainder)
 *
 * One implementation for the server (the create re-computes everything under the facture lock —
 * the authority) and for the avoir editor's preview (the figures the user confirms are the ones
 * the server stores). Moved here from `apps/server/src/services/avoir-calcul.js` (WP-29).
 */
import {
  appliquerPct,
  diviserArrondi,
  multiplier,
  somme,
  versCentiemesPct,
  versCentimes,
} from "./money.js";
import { ETAT_SUGGESTION_ECART, LIMITE_IMPUTATION, MODE_REPARTITION_AVOIR } from "./constantes.js";

const c = (v) => versCentimes(v ?? 0);

/**
 * F4 / F5 — the value of an avoir line, rounded half away from zero to the centime.
 * @param {{quantite: number, pu_net_ht: any, taux_tva: any}} l
 * @returns {{ht: number, tva: number, ttc: number}} centimes
 */
export function valeurLigneAvoirCentimes({ quantite, pu_net_ht, taux_tva }) {
  const ht = multiplier(quantite, c(pu_net_ht));
  const tva = appliquerPct(ht, versCentiemesPct(taux_tva ?? 0));
  return { ht, tva, ttc: ht + tva };
}

/** F5 — Σ of already rounded line values (centimes) → { ht, tva, ttc }. */
export function totauxValeursAvoir(valeurs) {
  const ht = somme(valeurs.map((v) => v.ht));
  const tva = somme(valeurs.map((v) => v.tva));
  return { ht, tva, ttc: ht + tva };
}

/**
 * F1 — the quantity still claimable as Manquant on a facture line: paid units not received, minus
 * the Manquant units already credited by live avoirs (UG shortfalls cannot occur, D-08).
 */
export function ecartQuantiteLigne({ quantite, quantite_recue, deja_credite = 0 }) {
  const recue = quantite_recue ?? quantite;
  return Math.max(0, quantite - recue - deja_credite);
}

/**
 * A-85 (WP-84) — the quantity of an invoice line an avoir produits may still credit: its paid units
 * (`quantite` — the billed units, UG never credited, A-54 (5)) minus every live avoir line already on
 * it, whatever its motif (several motifs per line, D-33). Cancelled avoirs are not counted by the
 * caller's read. Never negative.
 * @param {{quantite: number, deja_credite_total?: number}} l
 */
export function eligibleRestantLigne({ quantite, deja_credite_total = 0 }) {
  return Math.max(0, (quantite ?? 0) - (deja_credite_total ?? 0));
}

/**
 * A-84 / A-111 (WP-84, CP §5.3) — « Manquant : facturé non reçu » with « Déduire du stock »: the units
 * invoiced but never received entered the stock at validation only for the part beyond the delivery
 * gap (Qté payée − Qté reçue: units that never entered — an edited QTE reçue, A-75) not yet claimed by
 * a « missing » line; the correction takes out those that entered AND remain (`disponible` — the
 * paid units the lot can still give, holds excluded), never more, never a duplicate.
 * @param {{quantite: number, quantite_recue?: number|null, non_entrees_deja?: number, demande: number, disponible: number}} p
 *   `non_entrees_deja` = the units of the line's live « missing » avoir lines that did not move the stock
 * @returns {{ecart_livraison: number, entrees: number, a_corriger: number}} the claimed units inside the
 *   delivery gap (never entered), those that entered, and those the correction takes out now
 */
export function correctionFactureNonRecu({ quantite, quantite_recue, non_entrees_deja = 0, demande, disponible }) {
  const q = Math.max(0, demande ?? 0);
  const recue = quantite_recue ?? quantite;
  const ecartLibre = Math.max(0, (quantite ?? 0) - (recue ?? 0) - (non_entrees_deja ?? 0));
  const ecart_livraison = Math.min(q, ecartLibre);
  const entrees = q - ecart_livraison;
  return { ecart_livraison, entrees, a_corriger: Math.min(entrees, Math.max(0, disponible ?? 0)) };
}

/** The paid units of a facture line that may still be returned (Retour, OP-03-03: paid units only). */
export function retourMaxLigne({ quantite, quantite_recue, deja_retourne = 0 }) {
  return Math.max(0, (quantite_recue ?? quantite) - deja_retourne);
}

/**
 * OP-03-06 — a TOTAL TTC over the ticked factures: oldest first (date, then numero interne), each up
 * to its `restant` (centimes); what is left is a fournisseur-level credit (not imputed on any facture).
 * @param {number} totalCentimes
 * @param {Array<{id: string, date_facture: Date|string, numero_interne?: number, restant: number}>} factures
 * @returns {{parts: Map<string, number>, excedent: number}}
 */
export function repartirEcart(totalCentimes, factures) {
  const ordre = [...factures].sort((a, b) => {
    const d = new Date(a.date_facture) - new Date(b.date_facture);
    if (d !== 0) return d;
    return (a.numero_interne ?? 0) - (b.numero_interne ?? 0);
  });
  const parts = new Map();
  let reste = totalCentimes;
  for (const f of ordre) {
    const part = Math.max(0, Math.min(reste, f.restant));
    parts.set(f.id, part);
    reste -= part;
  }
  return { parts, excedent: reste };
}

/**
 * OP-03-07 — HT / TVA of an écart amount (TTC authoritative). When the facture's declared HT remisé
 * and TVA exist and the écarts are coherent (TTC écart > 0, components ≥ 0), split pro rata of the
 * declared-vs-computed écarts — the residual (timbre, arrondis) stays in the TTC only. Otherwise
 * (only the TTC was declared): pro rata of the facture's computed HT remisé / TVA (DECISIONS A-54).
 * @param {number} ttc centimes
 * @param {{montant_ht_remise_declare?: any, montant_tva_declare?: any, difference?: any,
 *          montant_ht?: any, montant_tva?: any, montant_ttc?: any}} facture
 * @returns {{ht: number, tva: number}} centimes; ht + tva ≤ ttc
 */
export function ventilerEcart(ttc, facture) {
  if (ttc <= 0) return { ht: 0, tva: 0 };
  const declareHt = facture.montant_ht_remise_declare;
  const declareTva = facture.montant_tva_declare;
  const ecartTtc = c(facture.difference);
  if (declareHt != null && declareTva != null && ecartTtc > 0) {
    const eHt = c(declareHt) - c(facture.montant_ht);
    const eTva = c(declareTva) - c(facture.montant_tva);
    if (eHt >= 0 && eTva >= 0 && eHt + eTva <= ecartTtc) {
      const ht = diviserArrondi(BigInt(ttc) * BigInt(eHt), BigInt(ecartTtc));
      const tva = diviserArrondi(BigInt(ttc) * BigInt(eTva), BigInt(ecartTtc));
      return ht + tva <= ttc ? { ht, tva } : { ht: ttc - tva, tva };
    }
  }
  const calcTtc = c(facture.montant_ttc);
  const calcTva = c(facture.montant_tva);
  if (calcTtc > 0 && calcTva >= 0 && calcTva <= calcTtc) {
    const tva = diviserArrondi(BigInt(ttc) * BigInt(calcTva), BigInt(calcTtc));
    return { ht: ttc - tva, tva };
  }
  return { ht: ttc, tva: 0 };
}

/**
 * The HT / TVA / TTC of a whole écart avoir, exactly as the server stores them: each facture's part
 * split on that facture (OP-03-07), the excess (fournisseur-level credit) split like the facture the
 * server reads first — the lowest id, its lock order (A-54).
 * @param {number} totalCentimes TOTAL TTC AVOIR
 * @param {Array<{id: string}>} factures the ticked factures (with the ventilerEcart fields)
 * @param {Map<string, number>} parts centimes imputed per facture id
 * @returns {{ht: number, tva: number, ttc: number, impute: number, excedent: number}}
 */
export function totauxAvoirEcart(totalCentimes, factures, parts) {
  let ht = 0;
  let tva = 0;
  let impute = 0;
  for (const f of factures) {
    const part = parts.get(f.id) ?? 0;
    const v = ventilerEcart(part, f);
    ht += v.ht;
    tva += v.tva;
    impute += part;
  }
  const excedent = Math.max(0, totalCentimes - impute);
  if (excedent > 0 && factures.length) {
    const premiere = [...factures].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))[0];
    const ex = ventilerEcart(excedent, premiere);
    ht += ex.ht;
    tva += ex.tva;
  }
  return { ht, tva, ttc: totalCentimes, impute, excedent };
}

/**
 * Refonte WP-81 (D-38, A-81) — HT / TVA of a fournisseur-level credit (TTC authoritative) over the
 * factures it names, without allocating it to any of them: no facture → HT = TTC, TVA 0 (no rate to
 * invent, as a ristourne — A-54 (7)); one facture → OP-03-07 on that facture; several → pro rata of
 * their combined computed TVA / TTC (one ratio for the whole amount, no per-facture split).
 * @param {number} ttc centimes
 * @param {Array<{montant_ttc?: any, montant_tva?: any}>} factures
 * @returns {{ht: number, tva: number}} centimes; ht + tva ≤ ttc (OP-03-07: a declared residual — timbre,
 *   rounding — stays in the TTC only)
 */
export function ventilerCreditFournisseur(ttc, factures = []) {
  if (ttc <= 0) return { ht: 0, tva: 0 };
  if (factures.length === 0) return { ht: ttc, tva: 0 };
  if (factures.length === 1) return ventilerEcart(ttc, factures[0]);
  const calcTtc = factures.reduce((s, f) => s + c(f.montant_ttc), 0);
  const calcTva = factures.reduce((s, f) => s + c(f.montant_tva), 0);
  if (calcTtc > 0 && calcTva >= 0 && calcTva <= calcTtc) {
    const tva = diviserArrondi(BigInt(ttc) * BigInt(calcTva), BigInt(calcTtc));
    return { ht: ttc - tva, tva };
  }
  return { ht: ttc, tva: 0 };
}

/**
 * Refonte WP-81 (CP §8, §8.2; D-38, A-81) — what a « crédit fournisseur » (Écart avoir) stores, given
 * its allocation method and the factures it names (0..n, the server reads them under their lock):
 *
 *   GLOBAL, 0 facture    a fournisseur-level credit: nothing imputed, HT / TVA by
 *                        `ventilerCreditFournisseur` (HT = TTC);
 *   GLOBAL, 1 facture    imputed up to the facture's open net dû (`net`), the rest fournisseur-level;
 *                        HT / TVA of the whole amount on that facture (OP-03-07);
 *   GLOBAL, 2+ factures  the factures are REFERENCES only (`est_reference`, imputed 0 — no allocation
 *                        is invented, supersedes OP-03-06); the whole amount is fournisseur-level,
 *                        HT / TVA on their combined ratio;
 *   PAR_FACTURE          the typed part of each facture (`part`) is kept (`reparti`, for the reports)
 *                        and imputed up to its open net dû; the rest of a part is fournisseur-level
 *                        (imputable later, TWP-57); total = Σ parts; each part's HT / TVA on its own
 *                        facture (OP-03-07). A part of 0 names its facture as a reference.
 *
 * The imputation HT / TVA of a facture never exceed those of its amount. Pure, integer centimes.
 * @param {{mode: string, total?: number, factures: Array<{id: string, net: number, part?: number}>}} p
 *   `total` (centimes) for GLOBAL; `net` = the open net dû of each facture for imputations (centimes);
 *   each facture also carries the `ventilerEcart` fields (declared / computed HT, TVA, TTC, difference)
 * @returns {{mode: string, lignes: Array<{id: string, reparti: number|null, impute: number, ht: number,
 *   tva: number, est_reference: boolean}>, ht: number, tva: number, ttc: number, impute: number,
 *   excedent: number}} centimes; `lignes` in the order of `factures`; `excedent` = the fournisseur-level part
 */
export function planCreditFournisseur({ mode, total = 0, factures = [] }) {
  const borne = (v) => Math.max(0, v ?? 0);
  if (mode === MODE_REPARTITION_AVOIR.PAR_FACTURE) {
    let ht = 0;
    let tva = 0;
    let ttc = 0;
    const lignes = factures.map((f) => {
      const part = borne(f.part);
      const impute = Math.min(part, borne(f.net));
      const vp = ventilerEcart(part, f);
      const vi = impute === part ? vp : ventilerEcart(impute, f);
      ht += vp.ht;
      tva += vp.tva;
      ttc += part;
      return { id: f.id, reparti: part, impute, ht: Math.min(vi.ht, vp.ht), tva: Math.min(vi.tva, vp.tva), est_reference: part === 0 };
    });
    // the residual of OP-03-07 (timbre, rounding) stays in the TTC: HT + TVA ≤ TTC per part
    const impute = lignes.reduce((s, l) => s + l.impute, 0);
    return { mode, lignes, ht, tva, ttc, impute, excedent: ttc - impute };
  }
  const montant = borne(total);
  if (factures.length === 1) {
    const f = factures[0];
    const impute = Math.min(montant, borne(f.net));
    const vt = ventilerCreditFournisseur(montant, [f]);
    const vi = impute === montant ? vt : ventilerCreditFournisseur(impute, [f]);
    return {
      mode,
      lignes: [{ id: f.id, reparti: null, impute, ht: Math.min(vi.ht, vt.ht), tva: Math.min(vi.tva, vt.tva), est_reference: impute === 0 }],
      ht: vt.ht,
      tva: vt.tva,
      ttc: montant,
      impute,
      excedent: montant - impute,
    };
  }
  const v = ventilerCreditFournisseur(montant, factures);
  return {
    mode,
    lignes: factures.map((f) => ({ id: f.id, reparti: null, impute: 0, ht: 0, tva: 0, est_reference: true })),
    ht: v.ht,
    tva: v.tva,
    ttc: montant,
    impute: 0,
    excedent: montant,
  };
}

/**
 * Refonte WP-81 (CP §8.1-§8.2) — the amount a crédit fournisseur may start from: each facture's
 * positive money écart still to credit (flagged above the tolerance at validation, D-09 / A-06, minus
 * what live Écart avoirs already credit on it — the P-8 cache rule), and their sum. Zero or negative
 * → no suggestion (`null`): the editor shows an information and an empty, editable amount (never a
 * negative prefill). A suggestion is never an eligibility condition nor a cap.
 * @param {Array<{id: string, difference?: any, avoir_financier_requis?: boolean, deja_credite?: any}>} factures
 * @returns {{factures: Array<{id: string, ecart: number|null, deja_credite: number, contribution: number,
 *   etat: string}>, total: number, suggestion: number|null}} centimes
 */
export function suggestionEcart(factures = []) {
  const lignes = factures.map((f) => {
    const ecart = f.difference == null ? null : c(f.difference);
    const deja = Math.max(0, c(f.deja_credite));
    let etat = ETAT_SUGGESTION_ECART.AUCUN;
    let contribution = 0;
    if (ecart != null && ecart < 0) etat = ETAT_SUGGESTION_ECART.NEGATIF;
    else if (ecart != null && ecart > 0 && !f.avoir_financier_requis) etat = ETAT_SUGGESTION_ECART.DANS_TOLERANCE;
    else if (ecart != null && ecart > 0) {
      contribution = Math.max(0, ecart - deja);
      etat = contribution > 0 ? ETAT_SUGGESTION_ECART.POSITIF : ETAT_SUGGESTION_ECART.DEJA_CREDITE;
    }
    return { id: f.id, ecart, deja_credite: deja, contribution, etat };
  });
  const total = lignes.reduce((s, l) => s + l.contribution, 0);
  return { factures: lignes, total, suggestion: total > 0 ? total : null };
}

/**
 * Trésorerie TWP-57 (TO-11; TA-92 (3)) — what one open facture can still take of an avoir's
 * fournisseur-level credit, in centimes: its reste dû (base − confirmed imputations − confirmed
 * settlement lines, `positionFacture`) minus the imputations INSTANCE avoirs have already committed on
 * it (their later confirmation must never push the facture below zero, A-101 (6)), floored at 0. On a
 * facture where THIS avoir carries a typed part (« Répartition par facture », M-22) the imputation
 * never exceeds that part (the part stays what was typed): at most `part − impute`.
 * @param {{reste: number, en_instance?: number, part?: number|null, impute?: number}} f centimes
 * @returns {{plafond: number, limite: "RESTE"|"PART"}} `limite` names the bound that applies
 */
export function plafondImputationCredit({ reste, en_instance = 0, part = null, impute = 0 }) {
  const ouvert = Math.max(0, (reste ?? 0) - Math.max(0, en_instance ?? 0));
  if (part == null) return { plafond: ouvert, limite: LIMITE_IMPUTATION.RESTE };
  const resteDePart = Math.max(0, part - Math.max(0, impute ?? 0));
  return resteDePart < ouvert
    ? { plafond: resteDePart, limite: LIMITE_IMPUTATION.PART }
    : { plafond: ouvert, limite: LIMITE_IMPUTATION.RESTE };
}

/**
 * Trésorerie TWP-57 (TA-92 (4)) — the oldest-first proposal of « Imputer le crédit » of an avoir: the
 * credit not imputed walks the open factures IN THE ORDER GIVEN (the caller sorts them by the
 * settlement's automatic order, TD-02), each up to its `plafond`, skipping the ones the automatic walk
 * leaves aside (`auto === false` — a « BL non facturé », TD-189), until the credit is spent. A proposal
 * only: the user confirms (or changes) each part, the server re-checks every cap under the locks.
 * @param {number} credit centimes
 * @param {Array<{id: string, plafond: number, auto?: boolean}>} factures
 * @returns {{lignes: Array<{id: string, montant: number}>, total: number, reste: number}} centimes;
 *   `reste` = what stays at the fournisseur level
 */
export function proposerImputationCredit(credit, factures = []) {
  const depart = Math.max(0, credit ?? 0);
  let reste = depart;
  const lignes = [];
  for (const f of factures) {
    if (reste <= 0) break;
    if (f.auto === false) continue;
    const part = Math.min(reste, Math.max(0, f.plafond ?? 0));
    if (part <= 0) continue;
    lignes.push({ id: f.id, montant: part });
    reste -= part;
  }
  return { lignes, total: depart - reste, reste };
}

/**
 * Trésorerie TWP-57 (TA-92 (5)) — the HT / TVA each imputed part of an avoir credit carries (TTC
 * authoritative): pro rata of what of the avoir's own HT / TVA no facture carries yet (`restant` =
 * the avoir's amounts − Σ its imputations), CUMULATED in the order of the parts, so that imputing the
 * whole remaining credit carries exactly the remaining HT and TVA — never more (Σ imputed ≤ the
 * avoir's amounts; per part HT + TVA ≤ TTC, the OP-03-07 residual — timbre, rounding — stays in the
 * TTC only). The avoir's rate is the avoir's (computed at its creation, A-107 (4)), never the target
 * facture's: the same credit carries the same TVA wherever it is imputed.
 * @param {{ttc: number, ht: number, tva: number}} restant centimes
 * @param {number[]} montants the TTC parts (centimes), Σ ≤ restant.ttc
 * @returns {Array<{ttc: number, ht: number, tva: number}>} centimes, in the order of `montants`
 */
export function ventilerImputationCredit(restant, montants = []) {
  const ttc = Math.max(0, restant?.ttc ?? 0);
  const tvaR = Math.min(Math.max(0, restant?.tva ?? 0), ttc);
  const htR = Math.min(Math.max(0, restant?.ht ?? 0), ttc - tvaR);
  const prorata = (cumul, v) => (ttc === 0 ? 0 : Math.min(v, diviserArrondi(BigInt(cumul) * BigInt(v), BigInt(ttc))));
  let cumul = 0;
  let htPrec = 0;
  let tvaPrec = 0;
  return montants.map((m) => {
    const part = Math.max(0, m ?? 0);
    cumul = Math.min(ttc, cumul + part);
    const tvaCum = Math.max(tvaPrec, prorata(cumul, tvaR));
    const htCum = Math.max(htPrec, Math.min(prorata(cumul, htR), cumul - tvaCum));
    const ligne = { ttc: part, ht: htCum - htPrec, tva: tvaCum - tvaPrec };
    htPrec = htCum;
    tvaPrec = tvaCum;
    return ligne;
  });
}
